import { basicTeamInfoFragment } from '../Teams/team.fragments'
import {
  playerCareerStatsFragment,
  playerCurrentTeamFragment,
  playerEventStatsFragment,
  playerExtraInfoFragment,
  playerFullStatsFragment,
} from './player.fragments'

export const playerProfileDataBySlugQuery = `
query($slug: ID!, $year: Int, $statsYear: Int, $eventYear: Int) {
  player(slug: $slug, forYear: $year) {
    clubTeam
    college
    collegeYear
    country
    countryCode
    dob
    age
    firstName
    handedness
    height
    highSchool
    hometown
    instagramUrl
    injuryDescription
    injuryStatus
    isCaptain
    jerseyNum
    lastName
    lastNameSuffix
    nickname
    officialId
    bio
    position
    positionName
    profileUrl
    scratch
    twitterUrl
    weight
    expFromYear
    slug
    status
    contractStatus
    allYears
    currentTeam {
      ...PlayerCurrentTeamFragment
      league
      shopifyJerseyLink
      shopifyJerseyImg
    }
    teams {
      officialId
      position
      positionName
      jerseyNum
      league
      year
    }
    careerStats {
      ...PlayerCareerStatsFragment
    }
    allSeasonStats {
      year
      seasonSegment
      teamId
      ...PlayerFullStatsFragment
    }
    extraInfo {
      ...PlayerExtraInfoFragment
    }
    accolades {
      awardName
      years
    }
    champSeries(year: $statsYear) {
      position
      stats {
        ...PlayerFullStatsFragment
      }
    }
    allEvents(year: $eventYear) {
      slugname
      startTime
      seasonSegment
      eventStatus
      gameStatus
      week
      seasonSegment
      description
      location
      urlStreaming
      urlTicket
      broadcaster
      homeScore
      visitorScore
      homeTeam {
        ...BasicTeamInfoFragment
      }
      awayTeam {
        ...BasicTeamInfoFragment
      }
      playerEventStats {
        ...PlayerEventStatsFragment
      }
    }
  }
}
  ${playerCurrentTeamFragment}
  ${playerFullStatsFragment}
  ${playerCareerStatsFragment}
  ${playerEventStatsFragment}
  ${playerExtraInfoFragment}
  ${basicTeamInfoFragment}
`

export const playerSeasonStatsBySlugQuery = `
query($slug: ID!, $year: Int, $statsYear: Int) {
  player(slug: $slug, forYear: $year) {
    champSeries(year: $statsYear) {
      stats {
      ...PlayerFullStatsFragment
      }
    }
    stats(year: $statsYear, segment: regular) {
      ...PlayerFullStatsFragment
    }
    postStats: stats(year: $statsYear, segment: post) {
      ...PlayerFullStatsFragment
    }
  }
}
${playerFullStatsFragment}
`

export const playerDataByIdQuery = `
query($id: ID!, $year: Int,  $segment: StatSegment!){
  player(id:$id, forYear: $year) {
    clubTeam
    college
    collegeYear
    country
    countryCode
    dob
    age
    firstName
    handedness
    height
    highSchool
    hometown
    instagramUrl
    injuryDescription
    injuryStatus
    isCaptain
    jerseyNum
    lastName
    lastNameSuffix
    nickname
    officialId
    bio
    position
    positionName
    profileUrl
    scratch
    twitterUrl
    weight
    expFromYear
    slug
  }
}
`

export const matchupPlayerDataByIdQuery = `
query($id: ID!, $year: Int,  $segment: StatSegment!){
  player(id:$id, forYear: $year) {
    clubTeam
    college
    collegeYear
    country
    countryCode
    dob
    age
    firstName
    handedness
    height
    highSchool
    hometown
    instagramUrl
    injuryDescription
    injuryStatus
    isCaptain
    jerseyNum
    lastName
    lastNameSuffix
    nickname
    officialId
    bio
    position
    positionName
    profileUrl
    scratch
    twitterUrl
    weight
    expFromYear
    slug
    currentTeam {
      league
      ...PlayerCurrentTeamFragment
    }
    stats(segment: $segment) {
      ...PlayerFullStatsFragment
    }
  }
}
${playerFullStatsFragment}
${playerCurrentTeamFragment}
`

export const playerInfoAndStatsByTeamQuery = `
query($id: ID, $year: Int, $statsYear: Int, $seasonSegment: StatSegment, $includeCS: Boolean!) {
  team(id:$id, forYear: $year) {
    champSeries(year: $year) @include(if: $includeCS) {
      roster {
        officialId
        slug
        profileUrl
        firstName
        lastName
        height
        age
        college
        collegeYear
        injuryStatus
        injuryDescription
        jerseyNum
        champSeries(year: $year) @include(if: $includeCS) {
          year
          position
          positionName
          stats {
            ...PlayerFullStatsFragment
          }
        }
      }
    }
    players {
      positionName
      officialId
      profileUrl
      jerseyNum
      firstName
      lastName
      lastNameSuffix
      experience
      position
      height
      weight
      age
      hometown
      country
      countryCode
      nickname
      highSchool
      college
      collegeYear
      handedness
      injuryStatus
      injuryDescription
      isCaptain
      scratch
      clubTeam
      dob
      twitterUrl
      instagramUrl
      bio
      slug
      rosterStatus
			currentTeam {
        ...PlayerCurrentTeamFragment
        league
        shopifyJerseyImg
        shopifyJerseyLink
      }
      stats(year: $statsYear, segment: $seasonSegment) {
        ...PlayerFullStatsFragment
      }
      champSeries(year: $year) @include(if: $includeCS) {
        position
        stats {
          ...PlayerFullStatsFragment
        }
      }
    }
  }
}
${playerFullStatsFragment}
${playerCurrentTeamFragment}
`

export const statLeadersQuery = `
query($year: Int!, $seasonSegment: SeasonSegment, $statList: [String], $limit: Int){
  playerStatLeaders(year: $year, limit: $limit, seasonSegment: $seasonSegment, statList: $statList) {
    officialId
    firstName
    lastName
    playerRank
    position
    profileUrl
    statNumValue
    statType
    statValue
    teamId
  }
}
`

export const playerTeammatesQuery = `
query($teamId: ID, $position: Position, $season: Int, $statType: StatType!) {
  allPlayers(teamId:$teamId, position:$position, season: $season, statType:$statType) {
    positionName
    officialId
    profileUrl
    jerseyNum
    firstName
    lastName
    lastNameSuffix
    experience
    position
    height
    weight
    age
    hometown
    country
    countryCode
    nickname
    highSchool
    college
    collegeYear
    handedness
    injuryStatus
    injuryDescription
    isCaptain
    scratch
    clubTeam
    dob
    twitterUrl
    instagramUrl
    bio
    slug
  }
}
`

export const fetchTeamLeaderByStat = `
query($teamId: ID, $sortBy: StatFieldName, $season: Int, $statType: StatType!, $limit: Int) {
  allPlayers(teamId:$teamId, sortBy:$sortBy, season: $season, statType:$statType, limit: $limit) {
		officialId
    firstName
    lastName
    profileUrl
    slug
    currentTeam{
      ...PlayerCurrentTeamFragment
    }
    stats {
      points
      onePointGoals
      twoPointGoals
      assists
      faceoffPct
      causedTurnovers
      savePct
    }
  }
}
${playerCurrentTeamFragment}
`

export const playerGameStatsBySlugQuery = `
query($slug: ID!, $year: Int, $includeCS: Boolean) {
  player(slug: $slug, forYear: $year) {
    allEvents(year: $year, includeCS: $includeCS) {
      slugname
      startTime
      seasonSegment
      eventStatus
      gameStatus
      week
      seasonSegment
      description
      location
      urlStreaming
      urlTicket
      broadcaster
      homeTeam {
        officialId
        fullName
        teamWins
        teamLosses
        teamTies
      }
      homeScore
      awayTeam {
        officialId
        fullName
        teamWins
        teamLosses
        teamTies
      }
      visitorScore
      playerEventStats {
        ...PlayerEventStatsFragment
      }
    }
  }
}
${playerEventStatsFragment}
`

//Getting player favorites by slug list
export const playerFavoritesQuery = `
query($slugList: [ID], $segment: StatSegment!){
  allPlayers(slugList:$slugList) {
    firstName
    lastName
    officialId
    profileUrl
    slug
    currentTeam {
      ...PlayerCurrentTeamFragment
    }
    stats(segment: $segment) {
      points
      onePointGoals
      twoPointGoals
      assists
      faceoffPct
      causedTurnovers
      savePct
      saves
    }
  }
}
${playerCurrentTeamFragment}
`

export const allPlayerInjuriesQuery = `
query($season: Int) {
  allPlayers(season: $season) {
    officialId
    firstName
    lastName
    profileUrl
    slug
    currentTeam {
      ...PlayerCurrentTeamFragment
    }
  }
}
  ${playerCurrentTeamFragment}
`

export const allPlayerTransactionsQuery = `
query{
  playerTransactions {
    id
    date
    officialId
    playerName
    position
    moveFrom
    moveTo
    details
  }
}
`

// All players for Contract Table
export const fetchPlayersWithContract = `
query($status: TeamStatus!, $season: Int) {
  allPlayers(status: $status, season: $season) {
		officialId
    firstName
    lastName
    profileUrl
    slug
    experience
    status
    contractStatus
    currentTeam {
      officialId
      fullName
      jerseyNum
      position
    }
  }
}
`

export const fetchPlayersOnTeamWithStatsSummary = `
query($year:Int) {
  allPlayers(season: $year, includeZPP: false, status: current) {
    officialId
    firstName
    lastName
    profileUrl
    college
    expFromYear
    experience
    currentTeam{
      officialId
      fullName
      position
      jerseyNum
      conference
    }
    stats(segment: regular){
      gamesPlayed
      statsSummary
      points
      groundBalls
      saves
      faceoffsWon
    }
    accolades{
      awardName
      years
    }
  }
}
`

export const allFreeAgentsForYearQuery = `
query($year: Int!) {
  freeAgents(year: $year){
    player{
      name
      firstName
      lastName
      slug
      profileUrl
      age
      experience
      position
      status
    }
    officialId
    newContractStatus
    newTeamId
    prevTeamId
    thirtyPercentThresholdMet
  }
}
`
